import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { realtime } from './realtime.js';

/** Авто-инвалидация TanStack Query при WS-событиях. */
export function useRealtimeInvalidation(workspaceSlug: string | undefined): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!workspaceSlug) return;
    let mounted = true;
    const unsubs: Array<() => void> = [];

    void (async () => {
      try {
        await realtime.connect();
      } catch (err) {
        console.warn('[realtime] connect failed', err);
        return;
      }
      if (!mounted) return;

      const invalidateTickets = (): void => {
        void qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] });
      };
      const invalidateMessages = (env: { data: { ticket_id?: string } }): void => {
        const id = env.data?.ticket_id;
        if (id) void qc.invalidateQueries({ queryKey: ['messages', workspaceSlug, id] });
      };
      const invalidateOperators = (): void => {
        void qc.invalidateQueries({ queryKey: ['operators', workspaceSlug] });
      };

      unsubs.push(
        realtime.on('ticket.created' as never, invalidateTickets),
        realtime.on('ticket.assigned' as never, invalidateTickets),
        realtime.on('ticket.reassigned' as never, invalidateTickets),
        realtime.on('ticket.status_changed' as never, invalidateTickets),
        realtime.on('ticket.priority_changed' as never, invalidateTickets),
        realtime.on('message.received' as never, (env) => {
          invalidateTickets();
          invalidateMessages(env as { data: { ticket_id?: string } });
        }),
        realtime.on('message.sent' as never, (env) =>
          invalidateMessages(env as { data: { ticket_id?: string } }),
        ),
        // Итог доставки ответа (в т.ч. «не доставлено» с причиной).
        realtime.on('message.delivered' as never, (env) =>
          invalidateMessages(env as { data: { ticket_id?: string } }),
        ),
        realtime.on('message.internal_note' as never, (env) =>
          invalidateMessages(env as { data: { ticket_id?: string } }),
        ),
        realtime.on('operator.status_changed' as never, invalidateOperators),
        // Добор пропущенного: на КАЖДОЕ (пере)подключение сокета рефетчим списки.
        // События транслируются одноразово (broadcast без реплея) — если сокет был
        // отключён в момент события (реконнект, спящая вкладка, опрос канала пришёлся
        // на разрыв), апдейт терялся до ручной перезагрузки. Теперь — добираем.
        realtime.onConnect(() => {
          invalidateTickets();
          invalidateOperators();
        }),
      );

      // Первичный refetch сразу после connect — покрывает race, когда первые
      // события приходят до регистрации listener'ов выше.
      invalidateTickets();
      invalidateOperators();
    })();

    return () => {
      mounted = false;
      for (const u of unsubs) u();
    };
  }, [workspaceSlug, qc]);
}
